import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, CalendarDays, Plus, Plane, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMyLeaveRequests } from '@/hooks/leave/useMyLeaveRequests';
import { useLeaveMutations } from '@/hooks/leave/useLeaveMutations';
import { useLeaveRequestValidation } from '@/hooks/leave/useLeaveRequestValidation';
import { EnhancedValidationFeedback } from '@/components/leave/EnhancedValidationFeedback';
import { LeaveBalanceSummary } from '@/components/leave/LeaveBalanceSummary';
import { BalanceProjection } from '@/components/leave/BalanceProjection';
import { LeaveRequestCard } from '@/components/cards/LeaveRequestCard';
import { LeaveRequestActions } from '@/components/leave/LeaveRequestActions';
import { AuditTrailViewer } from '@/components/leave/audit/AuditTrailViewer';
import type { LeaveRequest, CreateLeaveRequestInput } from '@/services/leave/types';
import { useMyAssignedLeaveTypes } from '@/hooks/leave/useMyAssignedLeaveTypes';

const HOURS_PER_DAY = 8;
const MAX_PART_DAY_HOURS = 7.5;

// Helper to calculate hours from time strings
const calculateHoursFromTimes = (start: string, end: string): number => {
  if (!start || !end) return 0;
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  return Math.max(0, (endMinutes - startMinutes) / 60);
};

const LeaveRequestPage = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form state
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isPartDay, setIsPartDay] = useState(false);
  const [requestedHours, setRequestedHours] = useState<number | ''>('');
  // New time picker state
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // React Query hooks
  const { 
    data: leaveRequests = [], 
    isLoading 
  } = useMyLeaveRequests(profile?.id || '');

  const { createLeaveRequest } = useLeaveMutations();
  
  // Assigned leave types hook - only shows leave types from active policy assignments
  const { 
    assignedLeaveTypes, 
    isLoading: isLoadingLeaveTypes, 
    hasAssignments 
  } = useMyAssignedLeaveTypes();

  // Calculate hours from times when they change
  const calculatedHours = isPartDay && startTime && endTime 
    ? calculateHoursFromTimes(startTime, endTime) 
    : (requestedHours || 0);

  // Real-time validation - pass hours and times for part-day
  const validationState = useLeaveRequestValidation(
    leaveType, 
    startDate, 
    isPartDay ? startDate : endDate,
    isPartDay ? calculatedHours : undefined,
    isPartDay ? startTime : undefined,
    isPartDay ? endTime : undefined
  );

  const handlePartDayChange = (checked: boolean) => {
    setIsPartDay(checked);
    if (checked && startDate) {
      setEndDate(startDate); // Lock end date to start date for part-day
    }
    if (!checked) {
      // Clear time fields when switching to full-day
      setStartTime('');
      setEndTime('');
      setRequestedHours('');
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (isPartDay) {
      setEndDate(value); // Keep end date synced for part-day
    }
  };

  const resetForm = () => {
    setLeaveType('');
    setStartDate('');
    setEndDate('');
    setReason('');
    setIsPartDay(false);
    setRequestedHours('');
    setStartTime('');
    setEndTime('');
  };

  const handleSubmit = () => {
    if (!leaveType || !startDate || (!isPartDay && !endDate)) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Part-day specific validations
    if (isPartDay) {
      if (!startTime || !endTime) {
        toast({
          title: 'Error',
          description: 'Please select start and end times for part-day requests',
          variant: 'destructive',
        });
        return;
      }

      const hours = calculateHoursFromTimes(startTime, endTime);
      if (hours <= 0) {
        toast({
          title: 'Error',
          description: 'End time must be after start time',
          variant: 'destructive',
        });
        return;
      }

      if (hours > MAX_PART_DAY_HOURS) {
        toast({
          title: 'Error',
          description: `Part-day leave cannot exceed ${MAX_PART_DAY_HOURS} hours. Use full-day leave instead.`,
          variant: 'destructive',
        });
        return;
      }

      if (!reason || reason.trim().length === 0) {
        toast({
          title: 'Error',
          description: 'Description is required for part-day requests',
          variant: 'destructive',
        });
        return;
      }
    }

    const effectiveEndDate = isPartDay ? startDate : endDate;

    if (!isPartDay && new Date(startDate) > new Date(effectiveEndDate)) {
      toast({
        title: 'Error',
        description: 'End date must be after start date',
        variant: 'destructive',
      });
      return;
    }

    const requestData: CreateLeaveRequestInput = {
      leave_type: leaveType,
      start_date: startDate,
      end_date: effectiveEndDate,
      reason: reason || undefined,
      is_part_day: isPartDay,
      requested_hours: isPartDay ? calculatedHours : undefined,
      start_time: isPartDay ? startTime : undefined,
      end_time: isPartDay ? endTime : undefined,
    };

    createLeaveRequest.mutate(requestData, {
      onSuccess: () => {
        resetForm();
        setIsDialogOpen(false);
      }
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  const calculateDays = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return diffDays;
  };

  const formatDuration = (request: LeaveRequest) => {
    if (request.requested_hours) {
      const days = request.requested_hours / HOURS_PER_DAY;
      return `${request.requested_hours}h (${days.toFixed(2)} days)`;
    }
    return `${calculateDays(request.start_date, request.end_date)} days`;
  };

  const formatTimeRange = (request: LeaveRequest) => {
    if (request.start_time && request.end_time) {
      return `${request.start_time.substring(0, 5)} - ${request.end_time.substring(0, 5)}`;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Enhanced Dashboard with Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="requests">My Requests</TabsTrigger>
              <TabsTrigger value="planner">Planner</TabsTrigger>
              <TabsTrigger value="audit">Audit</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <LeaveBalanceSummary showProjections={true} />
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>
                      Common leave management tasks
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button onClick={() => setIsDialogOpen(true)} className="w-full justify-start">
                      <Plus className="h-4 w-4 mr-2" />
                      New Leave Request
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="requests" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>My Leave Requests</CardTitle>
                  <CardDescription>
                    View and manage your leave requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setIsDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Leave Request
                  </Button>
                </CardContent>
              </Card>

              {leaveRequests && leaveRequests.length > 0 ? (
                <>
                  {/* Mobile view */}
                  <div className="md:hidden space-y-4">
                    {leaveRequests.map((request) => (
                      <LeaveRequestCard 
                        key={request.id} 
                        request={request}
                        getStatusBadgeVariant={getStatusBadgeVariant}
                        calculateDays={calculateDays}
                      />
                    ))}
                  </div>

                  {/* Desktop view */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Leave Type</TableHead>
                          <TableHead>Start Date</TableHead>
                          <TableHead>End Date</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leaveRequests.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell className="font-medium capitalize">
                              {request.leave_type}
                              {request.requested_hours && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  Part Day
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {format(new Date(request.start_date), 'MMM dd, yyyy')}
                              {formatTimeRange(request) && (
                                <span className="block text-xs text-muted-foreground">
                                  {formatTimeRange(request)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {format(new Date(request.end_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              {formatDuration(request)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={getStatusBadgeVariant(request.status)}>
                                {request.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <div className="truncate" title={request.reason || 'No reason provided'}>
                                {request.reason || 'No reason provided'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <LeaveRequestActions leaveRequest={request} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No leave requests found</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      You haven't submitted any leave requests yet.
                    </p>
                    <Button onClick={() => setIsDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create your first request
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="planner" className="space-y-6">
              <BalanceProjection />
            </TabsContent>
            
            <TabsContent value="audit" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>My Leave Accrual History</CardTitle>
                  <CardDescription>
                    View your personal leave accrual and deduction history
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AuditTrailViewer profileId={profile?.id} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Enhanced Leave Request Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Request Time Off</DialogTitle>
                <DialogDescription>
                  Fill out the form below to submit a new leave request. All fields marked with * are required.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="leave-type">Leave Type *</Label>
                  <Select 
                    value={leaveType} 
                    onValueChange={setLeaveType}
                    disabled={isLoadingLeaveTypes || !hasAssignments}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        isLoadingLeaveTypes 
                          ? "Loading..." 
                          : hasAssignments 
                            ? "Select leave type" 
                            : "No leave policies assigned"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {assignedLeaveTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isLoadingLeaveTypes && !hasAssignments && (
                    <p className="text-xs text-destructive">
                      You have no leave policies assigned. Please contact your administrator.
                    </p>
                  )}
                </div>

                {/* Part Day Checkbox */}
                <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/30">
                  <Checkbox
                    id="part-day"
                    checked={isPartDay}
                    onCheckedChange={handlePartDayChange}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="part-day"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Part Day Request
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Request a partial day (specific hours) instead of full days
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="start-date">{isPartDay ? 'Date *' : 'Start Date *'}</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                    />
                  </div>
                  {!isPartDay && (
                    <div className="grid gap-2">
                      <Label htmlFor="end-date">End Date *</Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* Time Pickers for Part Day */}
                {isPartDay && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="start-time">Start Time *</Label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="start-time"
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="end-time">End Time *</Label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="end-time"
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Calculated Hours Display for Part Day */}
                {isPartDay && startTime && endTime && (
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      Calculated hours: <strong>{calculatedHours.toFixed(1)}h</strong>
                      {calculatedHours > MAX_PART_DAY_HOURS && (
                        <span className="text-destructive ml-2">
                          (exceeds {MAX_PART_DAY_HOURS}h limit)
                        </span>
                      )}
                      {calculatedHours <= 0 && (
                        <span className="text-destructive ml-2">
                          (invalid time range)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="reason">
                    {isPartDay ? 'Description of Part Day * (e.g., "Morning appointment")' : 'Reason (Optional)'}
                  </Label>
                  <Textarea
                    id="reason"
                    placeholder={isPartDay 
                      ? "Describe which part of the day and why (required for part-day requests)..." 
                      : "Provide additional details about your leave request..."
                    }
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required={isPartDay}
                  />
                </div>

                {/* Enhanced validation feedback */}
                {(leaveType && startDate && (isPartDay || endDate)) && (
                  <EnhancedValidationFeedback 
                    errors={validationState.errors}
                    isValidating={validationState.isValidating}
                    isValid={validationState.canSubmit}
                    leaveType={leaveType}
                    startDate={startDate}
                    endDate={isPartDay ? startDate : endDate}
                    calculatedDays={validationState.requestedDays}
                    isPartDay={isPartDay}
                    requestedHours={isPartDay ? calculatedHours : undefined}
                  />
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={
                    !hasAssignments ||
                    createLeaveRequest.isPending || 
                    !validationState.canSubmit || 
                    (isPartDay && (!startTime || !endTime || !reason || calculatedHours <= 0 || calculatedHours > MAX_PART_DAY_HOURS))
                  }
                >
                  {createLeaveRequest.isPending ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default LeaveRequestPage;
